import React from 'react';
import {FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import type {DocumentoAdjuntoDTO, ProyectoFormDTO} from '../../../../api/Objetos';
import {documentosPublicosApi, reunionesApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {DateField, FieldGrid, NumberField, TextField, ToggleField} from '../../../../ui/form/fields';
import {
    EmptyState,
    FormSection,
    ItemCard,
    buildCardTitle,
    buildSummary,
    hasPersistedId,
    useProjectReadOnly,
    useProjectPublicView,
    BulkToggle
} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton, useCollectionSection} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import BarraCargando from "../../../../ui/BarraCargando";
import {useQuery} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {DocumentosAdjuntosPanel} from '../DocumentosAdjuntosPanel';

export function ReunionesSection(props: { proyectoId: number; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});
    const section = useCollectionSection({
        queryKey: ['admin', 'proyectos', 'reuniones'],
        proyectoId: props.proyectoId,
        getFn: reunionesApi.list,
        createFn: reunionesApi.create,
        updateFn: reunionesApi.update,
        deleteFn: reunionesApi.delete,
    });

    React.useEffect(() => {
        form.reset({...createEmptyProyecto(), reuniones: section.data ?? []});
    }, [form, section.data]);

    const handleSave = form.handleSubmit(async (value) => {
        await section.save(value.reuniones ?? []);
    });

    useRegisterSectionSave({
        id: 'reuniones',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <ReunionesSectionFields
                proyectoId={props.proyectoId}
                showIds={Boolean(props.showIds)}
                isLoading={section.isLoading}
                loadError={section.error}
            />
            {!readOnly && (
                <>
                    <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>
                </>
            )}
        </FormProvider>
    );
}

function ReunionesSectionFields(props: { proyectoId: number; showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const reuniones = useFieldArray({control: form.control, name: 'reuniones'});
    const showId = (path: string) => props.showIds && hasPersistedId(form.watch(path as never));
    const readOnly = useProjectReadOnly();
    const publicView = useProjectPublicView();

    const setAllPublic = (isPublic: boolean) => {
        reuniones.fields.forEach((field, index) => {
            form.setValue(`reuniones.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const currentReuniones = form.watch('reuniones') || [];

    return (
        <FormSection
            id="reuniones"
            title={t('reunionesSection.title', { count: reuniones.fields.length })}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <BulkToggle
                        label={t('reunionesSection.public')}
                        fields={currentReuniones}
                        onToggle={setAllPublic}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            {reuniones.fields.length === 0 && <EmptyState message={t('reunionesSection.noMeetings')}/>}
            {reuniones.fields.map((field, index) => (
                <ItemCard
                    key={field.id}
                    title={buildCardTitle(t, t('reunionesSection.meeting'), index, form.watch(`reuniones.${index}.descripcion`), form.watch(`reuniones.${index}.fecha`))}
                    summary={buildSummary(t, form.watch(`reuniones.${index}.fecha`), form.watch(`reuniones.${index}.descripcion`))}
                    onRemove={() => reuniones.remove(index)}
                    defaultOpen={!hasPersistedId(form.watch(`reuniones.${index}.id` as never))}
                    actions={!readOnly &&
                        <ToggleField
                            label={t('reunionesSection.public')}
                            name={`reuniones.${index}.publico`}
                            register={form.register}
                        />
                    }
                >
                    <FieldGrid>
                        {showId(`reuniones.${index}.id`) ?
                            <NumberField label={t('reunionesSection.id')} name={`reuniones.${index}.id`} register={form.register}/> : null}
                        <DateField label={t('reunionesSection.date')} name={`reuniones.${index}.fecha`} register={form.register}/>
                        <TextField label={t('reunionesSection.description')} name={`reuniones.${index}.descripcion`}
                                   register={form.register}/>
                        <TextField label={t('reunionesSection.meetingMinutesHyperlink')} name={`reuniones.${index}.hipervinculoActaReunion`}
                                   register={form.register}/>
                    </FieldGrid>
                    {hasPersistedId(form.watch(`reuniones.${index}.id` as never)) && !publicView ? (
                        <div className="adminNestedPanel">
                            <h3>Acta de la reunión</h3>
                            <DocumentosAdjuntosPanel proyectoId={props.proyectoId} ambito="ACTA_REUNION"
                                subcontratistaId={Number(form.watch(`reuniones.${index}.id`))}
                                accept=".pdf" maxFiles={1} compact/>
                        </div>
                    ) : publicView && hasPersistedId(form.watch(`reuniones.${index}.id` as never)) ? (
                        <PublicMeetingMinutes proyectoId={props.proyectoId} reunionId={Number(form.watch(`reuniones.${index}.id`))}/>
                    ) : !readOnly ? <p className="muted">Guarda primero la reunión para poder adjuntar el acta.</p> : null}
                </ItemCard>
            ))}

            {!readOnly ? (
                <button className="btn btnSecondary" type="button" onClick={() => reuniones.append({
                    id: 0,
                    fecha: '',
                    descripcion: '',
                    hipervinculoActaReunion: '',
                    publico: false,
                })}>
                    {t('reunionesSection.addMeeting')}
                </button>
            ) : null}
        </FormSection>
    );
}

function PublicMeetingMinutes({proyectoId, reunionId}: {proyectoId: number; reunionId: number}) {
    const documents = useQuery({
        queryKey: ['public', 'meeting-minutes', proyectoId, reunionId],
        queryFn: () => documentosPublicosApi.listar(proyectoId, 'ACTA_REUNION', reunionId),
    });
    if (!documents.data?.length) return null;
    return <div className="documentList">{documents.data.map((document: DocumentoAdjuntoDTO) => (
        <a className="btn btnSecondary" key={document.id}
            href={documentosPublicosApi.contenidoUrl(proyectoId, document.id!)} target="_blank" rel="noreferrer">
            Consultar acta PDF
        </a>
    ))}</div>;
}
